import * as Dialog from "@radix-ui/react-dialog"
import { Eye, EyeOff, Loader2, Trash2, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { SearchableCombobox, type SearchableComboboxOption } from "@/components/ui/searchable-combobox"
import { Button, Textarea } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { confirm } from "@/lib/confirm"
import { formatShotLabel } from "@/lib/shot-label"
import {
  fetchSceneOptions,
  getCsrfToken,
  patchShotIntentSummary,
  patchShotScene,
  type WorkspaceSceneOption,
} from "@/lib/workspace-api"
import { cn } from "@/lib/utils"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  shotId: number
  sceneId: number
  label: string
  description: string | null
  hidden: boolean
  onDataChanged: () => void
  onDeleted: () => void
}

function sceneOptionLabel(sc: WorkspaceSceneOption): string {
  const title = sc.title?.trim()
  const scenePart = title ? `${sc.number} · ${title}` : String(sc.number)
  return scenePart
}

function sceneOptionGroup(sc: WorkspaceSceneOption): string {
  const epTitle = sc.episode_title?.trim()
  const ep = epTitle ? `E${sc.episode_number} · ${epTitle}` : `E${sc.episode_number}`
  return `S${sc.season_number}${ep}`
}

export function ShotEditDialog({
  open,
  onOpenChange,
  projectId,
  shotId,
  sceneId,
  label,
  description,
  hidden,
  onDataChanged,
  onDeleted,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const [draft, setDraft] = useState(description ?? "")
  const [draftSceneId, setDraftSceneId] = useState(String(sceneId))
  const [displayLabel, setDisplayLabel] = useState(label)
  const [scenes, setScenes] = useState<WorkspaceSceneOption[]>([])
  const [scenesLoading, setScenesLoading] = useState(false)
  const [busy, setBusy] = useState<"save" | "toggle" | "delete" | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDraft(description ?? "")
    setDraftSceneId(String(sceneId))
    setDisplayLabel(label)
    setError(null)
  }, [open, description, sceneId, label])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setScenesLoading(true)
    void fetchSceneOptions(projectId)
      .then((rows) => {
        if (!cancelled) setScenes(rows)
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t("edit.moveFailed"))
        }
      })
      .finally(() => {
        if (!cancelled) setScenesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId, t])

  const sceneOptions = useMemo((): SearchableComboboxOption[] => {
    return scenes.map((sc) => ({
      value: String(sc.id),
      label: sceneOptionLabel(sc),
      group: sceneOptionGroup(sc),
    }))
  }, [scenes])

  const sceneChanged = draftSceneId !== String(sceneId)

  const handleSave = async () => {
    if (busy) return
    setBusy("save")
    setError(null)
    try {
      await patchShotIntentSummary(projectId, shotId, draft)
      if (sceneChanged) {
        const moved = await patchShotScene(projectId, shotId, Number(draftSceneId))
        const nextLabel = formatShotLabel(moved.scene_number, moved.number)
        setDisplayLabel(nextLabel)
        toast.success(t("edit.movedToast", { label: nextLabel }))
      } else {
        toast.success(t("common.saved"))
      }
      onDataChanged()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.saveFailed"))
    } finally {
      setBusy(null)
    }
  }

  const handleToggleVisibility = async () => {
    if (busy) return
    setBusy("toggle")
    setError(null)
    try {
      const csrf = getCsrfToken()
      const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/toggle_visibility`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
        },
        body: JSON.stringify({}),
      })
      if (!res.ok) throw new Error(t("edit.toggleVisibilityFailed", { status: res.status }))
      toast.success(hidden ? t("common.shotShown") : t("common.shotHidden"))
      onDataChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : t("edit.toggleVisibilityFailed", { status: "?" }))
    } finally {
      setBusy(null)
    }
  }

  const handleDelete = async () => {
    if (busy) return
    if (
      !(await confirm({
        message: t("common.deleteConfirm"),
        confirmLabel: t("common.yesDelete"),
        tone: "danger",
      }))
    ) {
      return
    }
    setBusy("delete")
    setError(null)
    try {
      const csrf = getCsrfToken()
      const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrf,
        },
      })
      if (!res.ok) throw new Error(t("edit.deleteFailed", { status: res.status }))
      toast.success(t("common.shotDeleted"))
      onDeleted()
    } catch (e) {
      setError(e instanceof Error ? e.message : t("edit.deleteFailed", { status: "?" }))
      setBusy(null)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[110] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            "ui-dialog-content fixed top-1/2 left-1/2 z-[111] w-[min(92vw,440px)] -translate-x-1/2 -translate-y-1/2",
            "rounded-lg border border-ui-border bg-ui-bg-elevated p-5 shadow-2xl shadow-black/60 focus:outline-none",
          )}
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <Dialog.Title className="text-base font-semibold text-ui-text-primary">
              {t("edit.title", { label: displayLabel })}
            </Dialog.Title>
            <Dialog.Close
              type="button"
              className="shrink-0 rounded-lg p-1.5 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
              aria-label={t("common.close")}
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          <label className="mb-1.5 block text-xs font-medium text-ui-text-secondary">{t("edit.sceneLabel")}</label>
          <SearchableCombobox
            value={draftSceneId}
            onChange={setDraftSceneId}
            options={sceneOptions}
            placeholder={scenesLoading ? "…" : t("edit.sceneSearchPlaceholder")}
            disabled={!!busy || scenesLoading}
            className="w-full"
            dropdownAutoWidth
          />
          <p className="mt-1.5 text-[11px] leading-snug text-ui-text-muted">{t("edit.sceneHint")}</p>

          <label className="mt-3 mb-1.5 block text-xs font-medium text-ui-text-secondary">
            {t("edit.descriptionLabel")}
          </label>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("placeholders.intentSummary")}
            rows={4}
            className="w-full resize-none"
            disabled={busy === "save"}
          />

          {error && <p className="mt-2 text-xs text-ui-status-failed">{error}</p>}

          <div className="mt-3 flex justify-end">
            <Button type="button" size="sm" disabled={!!busy} onClick={handleSave}>
              {busy === "save" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              {t("common.save")}
            </Button>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-ui-border pt-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!!busy}
              onClick={handleToggleVisibility}
              className={
                hidden
                  ? "text-ui-status-approved hover:text-ui-status-approved"
                  : "text-ui-text-secondary hover:text-ui-status-failed"
              }
            >
              {busy === "toggle" ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : hidden ? (
                <Eye className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <EyeOff className="mr-1.5 h-3.5 w-3.5" />
              )}
              {hidden ? t("common.showShot") : t("common.hideShot")}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-ui-text-muted hover:text-ui-status-failed"
              disabled={!!busy}
              onClick={handleDelete}
            >
              {busy === "delete" ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t("common.delete")}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
